import React, { useEffect, useState } from "react";
import Lottie from "lottie-react";
import { css } from "@emotion/react";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import loading from "@src/assets/Loading.json";
import { useFindSubsInfo, useFindSubsRefundAmount, useRemoveUserSubsPayDeposit } from "@src/hooks/subscribe/useSubscribe";
import { getDeviceType, transferToNative } from "@src/lib/WebAppBridge";
import ConfirmModal from "@src/components/popup/ConfirmModal";

import useCallToken from "@src/hooks/useCallToken";
import styled from "styled-components";
import TerminateNow from "./TerminateNow";
import MgDialog from "@src/components/Dialog";

interface Props {}

const UnSubscriptionWithDraw: React.FC<Props> = () => {
    const { token } = useCallToken();

    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

    const [isOpenPeneltyPaySuccess, setIsOpenPeneltyPaySuccess] = useState<boolean>(false);

    const [isOpenPayDepositModal, setIsOpenPayDepositModal] = useState<boolean>(false);

    // 즉시 해지시 환불액
    const { data: subsRefundAmount, refetch: refetchSubsRefundAmount } = useFindSubsRefundAmount();

    // 유저 구독 정보
    const { data: userSubsInfo, refetch: refetchSubsInfo, isLoading: isLoadingSubsInfo } = useFindSubsInfo();

    const isInAppSubs = userSubsInfo?.isInAppSubs;
    useEffect(() => {
        if (token.length) {
            refetchSubsInfo();
            refetchSubsRefundAmount();
        }
    }, [token]);

    //* 유저 구독 즉시 해지 (월 결제 위약금이 있을때)
    const monthlyUserSubscriptionRemove = useRemoveUserSubsPayDeposit(
        (res) => {
            if (res.status === 200) {
                setIsOpenPeneltyPaySuccess(true);
            }
            if (res.status === 500) {
                setShowErrorModal(true);
            }
        },
        (err) => {
            const newErr: any = err?.response?.data;
            console.log("위약금 결제 실패 >>", newErr?.message);
            setShowErrorModal(true);
        },
    );

    return (
        <>
            <div css={rootBoxStyle}>
                <div css={headStyle}>
                    <ArrowBackIosNewRoundedIcon
                        onClick={() => {
                            return transferToNative(false, "closeUnsubscribeView");
                        }}
                    />
                    <p className="name">해지하기</p>
                    <p />
                </div>

                <div css={contentStyle}>
                    {isLoadingSubsInfo || !userSubsInfo ? (
                        <div css={lottieBoxStyle}>
                            <Lottie className="lottie" animationData={loading} />
                        </div>
                    ) : (
                        <>
                            {/* 즉시 해지 */}
                            <TerminateNow
                                isWithDraw={true}
                                subsRefundAmount={subsRefundAmount}
                                userSubsInfo={userSubsInfo}
                                onCloseView={() => transferToNative(false, "closeUnsubscribeView")}
                                onClickUnsubs={() => {
                                    // 해당 화면은 penaltyType === 5 일때만 뜨는 화면
                                    refetchSubsInfo?.();
                                    if (subsRefundAmount?.penaltyType === 5) {
                                        setIsOpenPayDepositModal(true);
                                        return;
                                    }
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* 위약금 인출 완료 모달 */}
            {isOpenPeneltyPaySuccess && (
                <MgDialog
                    open={isOpenPeneltyPaySuccess}
                    onCloseView={() => {
                        transferToNative("", "onRedirectWithDraw");
                    }}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">해지 완료</div>
                            <div className="SubTitle">
                                결제하신 카드로 위약금이 인출되었으며,
                                <br />
                                이어서 탈퇴를 진행하겠습니다.
                            </div>
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={ConfirmStyle}
                    confirmText="확인"
                    useOnCloseView={false}
                />
            )}

            {/* 위약금 결제된다는 안내 모달 */}
            {isOpenPayDepositModal && (
                <MgDialog
                    open={isOpenPayDepositModal}
                    onCloseView={() => setIsOpenPayDepositModal(false)}
                    contents={
                        <ImmediateTerminateNoneDepositModal>
                            <div className="Title">정말로 해지하시겠습니까?</div>
                            {!isInAppSubs ? (
                                <div className="SubTitle">
                                    * 결제하신 카드로 위약금({subsRefundAmount?.penaltyPrice?.toLocaleString()}원)을 <br />
                                    추가 결제합니다.
                                    <br />
                                    <br />* 위약금 결제 이후 탈퇴가 이어서 진행됩니다. 탈퇴시 즉시 처리되므로 구독 기간이
                                    남아있었더라도 재사용이 불가합니다.
                                </div>
                            ) : (
                                <div className="SubTitle">
                                    * 위약금 ({subsRefundAmount?.penaltyPrice?.toLocaleString()}원)이 발생하여 등록된 고객님의
                                    번호로 안내드릴 예정입니다.
                                    <br />
                                    <br />* 탈퇴는 즉시 처리되므로 구독 기간이 남아있었더라도 재사용이 불가합니다.
                                    <br />
                                    <br />
                                    <span style={{ color: "#f41039" }}>
                                        <span style={{ fontWeight: "700" }}>📍주의사항</span> Apple 인앱 결제 상품이므로, <br />{" "}
                                        반드시 본인의 계정 &gt; 구독에서 <br />
                                        직접 구독을 해지 해야합니다.
                                    </span>
                                    <br />
                                </div>
                            )}
                        </ImmediateTerminateNoneDepositModal>
                    }
                    width="284px"
                    confirmStyle={PayConfirmStyle}
                    cancelStyle={ConfirmStyle}
                    onClickSubmit={() => {
                        setIsOpenPayDepositModal(false);
                        if (!isInAppSubs) {
                            monthlyUserSubscriptionRemove?.mutate({ penaltyPrice: Number(subsRefundAmount?.penaltyPrice) });
                        } else {
                            transferToNative("", "onRedirectWithDraw");
                        }
                    }}
                    confirmText="해지 및 탈퇴"
                    cancelText="취소"
                    useOnCloseView={false}
                />
            )}

            {showErrorModal && (
                <ConfirmModal
                    resultMessage={
                        <>
                            <p css={dialogTitleStyle}>위약금 결제 실패</p>
                            <p css={dialogContentStyle}>
                                * 위약금 ({subsRefundAmount?.penaltyPrice?.toLocaleString()}원)이 미결제되어 등록된 고객님의
                                번호로 안내드릴 예정입니다.
                                <br />
                                <br />* 탈퇴는 즉시 처리되므로 구독 기간이 남아있었더라도 재사용이 불가합니다.
                            </p>
                        </>
                    }
                    onConfirm={() => {
                        setShowErrorModal(false);
                        transferToNative("", "onRedirectWithDraw");
                    }}
                />
            )}
        </>
    );
};
export default UnSubscriptionWithDraw;

const ImmediateTerminateNoneDepositModal = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding-bottom: 10px;

    text-align: center;

    .Title {
        color: #0c002c;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px;
    }

    .SubTitle {
        color: #0c002c;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const ModalContentStyle = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    .titleText {
        text-align: center;
        font-size: 16px;
        font-weight: 400;
        line-height: 26px;
    }
    .titleDescText {
        margin-bottom: 6px;
    }
    .priceText {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .usedText {
        padding-bottom: 8px;
        border-bottom: 0.6px solid #8d8d8d;
    }
    .refundText {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-weight: 700;
    }

    .refundWarningText {
        color: #f41039;
        text-align: center;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const ReservationModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding-bottom: 10px;

    text-align: center;

    .Title {
        color: #0c002c;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px;
    }

    .SubTitle {
        color: #0c002c;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
    }
`;

const LottieBoxStyle = styled.div`
    padding: 80px 0 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    .lottie {
        width: 140px;
        height: 140px;
    }
`;

const ConfirmStyle = css`
    font-size: 18px;
`;

const PayConfirmStyle = css`
    font-size: 18px;
    background-color: #f22 !important;
    color: #fff !important;
    width: fit-content !important;
    white-space: nowrap !important;
`;

const rootBoxStyle = css`
    height: 100dvh;
    height: 100vh;
    overflow: hidden;
`;

const headStyle = css`
    display: flex;
    padding: 14px;
    justify-content: space-between;
    align-items: center;
    color: #0c002c;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    height: 56px;
    position: absolute;
    top: 0;
    width: 100vw;
    background-color: #ffffff;
`;

const contentStyle = css`
    height: 100%;
    margin-top: 60px;
`;

const lottieBoxStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60vh;
    width: 100%;
    & > .lottie {
        width: 30%;
    }
`;

const dialogTitleStyle = css`
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
`;

const dialogContentStyle = css`
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    margin: 8px 0 18px;
    word-break: break-word;
`;
